import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";

type AccessProfile = {
  id: number;
  name: string;
  description?: string | null;
};

type Permission = {
  id: number;
  code: string;
  description?: string | null;
};

export const AccessProfilesPage: React.FC = () => {
  const { client } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProfile, setActiveProfile] = useState<AccessProfile | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = useCallback(async () => {
    const response = await client.get("/modules/access-profiles/permissions");
    if (!response.ok) {
      return;
    }
    const payload = (await response.json()) as { data: Permission[] };
    setPermissions(payload.data ?? []);
  }, [client]);

  useEffect(() => {
    void loadPermissions();
  }, [loadPermissions]);

  const openPermissionsModal = useCallback(
    async (profile: AccessProfile) => {
      setActiveProfile(profile);
      setError(null);
      const response = await client.get(`/modules/access-profiles/${profile.id}`);
      if (!response.ok) {
        setError("Falha ao carregar permissoes.");
        return;
      }
      const payload = (await response.json()) as {
        data: {
          permissions: Array<{ permission: Permission }>;
        };
      };
      const ids = payload.data.permissions.map((item) => item.permission.id);
      setSelectedPermissionIds(ids);
      setModalOpen(true);
    },
    [client]
  );

  const togglePermission = (permissionId: number) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const savePermissions = useCallback(async () => {
    if (!activeProfile) {
      return;
    }
    setSaving(true);
    setError(null);
    const response = await client.put(
      `/modules/access-profiles/${activeProfile.id}/permissions`,
      { permissionIds: selectedPermissionIds }
    );
    if (!response.ok) {
      setError("Falha ao salvar permissoes.");
    } else {
      setModalOpen(false);
    }
    setSaving(false);
  }, [activeProfile, client, selectedPermissionIds]);

  const permissionList = useMemo(
    () => permissions.sort((a, b) => a.code.localeCompare(b.code)),
    [permissions]
  );

  return (
    <>
      <CrudPage<AccessProfile>
        title="Perfis de acesso"
        endpoint="/modules/access-profiles"
        searchPlaceholder="Buscar perfil..."
        columns={[
          { label: "Nome", render: (item) => item.name },
          { label: "Descricao", render: (item) => item.description ?? "-" },
        ]}
        formFields={[
          { name: "name", label: "Nome", type: "text", required: true },
          { name: "description", label: "Descricao", type: "text" },
        ]}
        initialValues={{
          name: "",
          description: "",
        }}
        mapToPayload={(values) => ({
          name: values.name,
          description: values.description || null,
        })}
        permissionWrite="ADMIN"
        customActions={(item) => (
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={() => openPermissionsModal(item)}
          >
            Permissoes
          </button>
        )}
      />

      {modalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Permissoes - {activeProfile?.name ?? ""}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setModalOpen(false)}
                    disabled={saving}
                  />
                </div>
                <div className="modal-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  <div className="d-flex flex-column gap-2">
                    {permissionList.map((permission) => (
                      <label key={permission.id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedPermissionIds.includes(permission.id)}
                          onChange={() => togglePermission(permission.id)}
                          disabled={saving}
                        />
                        <span className="form-check-label">
                          <strong>{permission.code}</strong>
                          {permission.description ? ` - ${permission.description}` : ""}
                        </span>
                      </label>
                    ))}
                    {permissionList.length === 0 && (
                      <div className="text-muted">Nenhuma permissao cadastrada.</div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setModalOpen(false)}
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={savePermissions}
                    disabled={saving}
                  >
                    {saving ? "Salvando..." : "Salvar permissoes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};
